import { useState, useRef, useEffect } from 'react';
import { Sun, Moon, Bell, LogOut, Settings, Menu, MessageSquare, CalendarClock, BellOff } from 'lucide-react';
import { useApp } from '../context/AppContext';
import api from '../Server';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60)   return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return `${Math.floor(diff / 86400)} day${Math.floor(diff / 86400) > 1 ? 's' : ''} ago`;
}

export default function Header({ activePage, onNavigate, onToggle }) {
  const { theme, toggleTheme, currentUser, logout } = useApp();
  const [rotating, setRotating]     = useState(false);
  const [showNotif, setShowNotif]   = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotif, setLoadingNotif]   = useState(false);
  const notifRef   = useRef(null);
  const profileRef = useRef(null);

  const pageTitles = {
    dashboard:    'Dashboard',
    services:     'Service Master',
    enquiries:    'Enquiries',
    demos:        'Demo Schedules',
    testimonials: 'Testimonials',
    settings:     'Settings',
  };

  // ── Fetch real notifications from enquiries + demos APIs ──────────────────
  const fetchNotifications = async () => {
    setLoadingNotif(true);
    try {
      const [enqRes, demoRes] = await Promise.allSettled([
        api.get('/message/getMessages'),
        api.get('/booking/getDemoBookings'),
      ]);

      const notifs = [];

      // Enquiries → latest 3
      if (enqRes.status === 'fulfilled') {
        const raw = enqRes.value.data;
        const list = Array.isArray(raw) ? raw
          : Array.isArray(raw?.data)     ? raw.data
          : Array.isArray(raw?.messages) ? raw.messages
          : Array.isArray(raw?.result)   ? raw.result
          : [];
        [...list].reverse().slice(0, 3).forEach(e => {
          notifs.push({
            id:   `enq-${e.id}`,
            type: 'enquiry',
            text: `New enquiry from ${e.name || e.full_name || 'Unknown'}`,
            sub:  e.subject || e.email || '',
            time: timeAgo(e.createdAt || e.created_at),
            page: 'enquiries',
          });
        });
      }

      // Demos → latest 3
      if (demoRes.status === 'fulfilled') {
        const raw = demoRes.value.data;
        const list = Array.isArray(raw) ? raw
          : Array.isArray(raw?.data)     ? raw.data
          : Array.isArray(raw?.bookings) ? raw.bookings
          : Array.isArray(raw?.result)   ? raw.result
          : [];
        [...list].reverse().slice(0, 3).forEach(d => {
          notifs.push({
            id:   `demo-${d.id}`,
            type: 'demo',
            text: `Demo booked by ${d.full_name || d.name || 'Unknown'}`,
            sub:  d.company || d.booking_date || '',
            time: timeAgo(d.createdAt || d.created_at),
            page: 'demos',
          });
        });
      }

      setNotifications(notifs);
    } catch {
      setNotifications([]);
    } finally {
      setLoadingNotif(false);
    }
  };

  // Fetch on mount
  useEffect(() => { fetchNotifications(); }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current   && !notifRef.current.contains(e.target))   setShowNotif(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfile(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleThemeToggle = () => {
    setRotating(true);
    toggleTheme();
    setTimeout(() => setRotating(false), 500);
  };

  const handleNotifOpen = () => {
    setShowNotif(v => !v);
    setShowProfile(false);
    // Refresh on open
    if (!showNotif) fetchNotifications();
  };

  const initial = currentUser?.name?.[0]?.toUpperCase() || 'A';
  const unread  = notifications.length;

  return (
    <header className="header">
      <div className="header-left">
        <button className="header-btn" onClick={onToggle} title="Toggle sidebar">
          <Menu size={20} />
        </button>
        <div>
          <div className="page-title">{pageTitles[activePage] || 'Dashboard'}</div>
          <div className="breadcrumb">Admin Panel / {pageTitles[activePage] || 'Dashboard'}</div>
        </div>
      </div>

      <div className="header-right">

        {/* Theme Toggle */}
        <button
          className={`header-btn theme-toggle${rotating ? ' rotating' : ''}`}
          onClick={handleThemeToggle}
          title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
        >
          {theme === 'light' ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* Notifications */}
        <div ref={notifRef} style={{ position: 'relative' }}>
          <button className="header-btn" onClick={handleNotifOpen} title="Notifications">
            <Bell size={18} />
            {unread > 0 && <span className="notif-badge" />}
          </button>

          {showNotif && (
            <div className="notif-dropdown">
              {/* Header row */}
              <div className="notif-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Notifications</span>
                {unread > 0 && (
                  <span style={{
                    background: 'var(--primary)', color: '#fff',
                    borderRadius: 99, fontSize: 10, fontWeight: 700,
                    padding: '2px 7px',
                  }}>
                    {unread}
                  </span>
                )}
              </div>

              {/* Body */}
              {loadingNotif ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
                  Loading...
                </div>
              ) : notifications.length === 0 ? (
                <div style={{
                  padding: '32px 20px', textAlign: 'center',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                }}>
                  <BellOff size={28} style={{ color: 'var(--text-secondary)', opacity: 0.4 }} />
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>
                    No notifications found
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', opacity: 0.7 }}>
                    New enquiries and demos will appear here
                  </div>
                </div>
              ) : (
                notifications.map(n => (
                  <div
                    className="notif-item"
                    key={n.id}
                    onClick={() => { onNavigate(n.page); setShowNotif(false); }}
                    style={{ cursor: 'pointer' }}
                  >
                    {/* Icon */}
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: n.type === 'enquiry' ? 'rgba(59,130,246,0.1)' : 'rgba(139,92,246,0.1)',
                      color: n.type === 'enquiry' ? '#3B82F6' : '#8B5CF6',
                    }}>
                      {n.type === 'enquiry'
                        ? <MessageSquare size={15} />
                        : <CalendarClock size={15} />
                      }
                    </div>

                    {/* Text */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        color: 'var(--text-primary)', fontSize: 13,
                        fontWeight: 600, marginBottom: 2,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {n.text}
                      </div>
                      {n.sub && (
                        <div style={{
                          fontSize: 11.5, color: 'var(--text-secondary)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {n.sub}
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3, opacity: 0.7 }}>
                        {n.time}
                      </div>
                    </div>

                    {/* Unread dot */}
                    <span className="notif-dot" style={{ flexShrink: 0, marginTop: 6 }} />
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Profile */}
        <div ref={profileRef} style={{ position: 'relative' }}>
          <div
            className="profile-avatar"
            onClick={() => { setShowProfile(v => !v); setShowNotif(false); }}
            title="Profile"
          >
            {currentUser?.image
              ? <img src={currentUser.image} alt="Profile" />
              : initial}
          </div>
          {showProfile && (
            <div className="profile-dropdown">
              <div className="profile-dropdown-header">
                <div className="profile-dropdown-name">{currentUser?.name}</div>
                <div className="profile-dropdown-email">{currentUser?.email}</div>
              </div>
              <div className="dropdown-item" onClick={() => { onNavigate('settings'); setShowProfile(false); }}>
                <Settings size={15} /> Settings
              </div>
              <div className="dropdown-item danger" onClick={logout}>
                <LogOut size={15} /> Logout
              </div>
            </div>
          )}
        </div>

      </div>
    </header>
  );
}
